/**
 * @module components/analysis/AudienceLearnMore
 * @description
 *   Inline "View" panel under the Primary target / Primary market card.
 *   Age bar chart + gender donut from demographics priors (audiencePackId).
 *   Tooltips sit top-right of the pointer; age hit target is the filled bar
 *   plus padding/min size so short bars stay tappable. Desktop = hover;
 *   compact = tap/click. Charts mount after panelReady.
 *
 * @see docs/SUBGENRE_AUDIENCE_RESEARCH.md
 */

import React, { createContext, useContext, useMemo, useRef, useState } from 'react';
import { Minimize2, Users } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Sector,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, IconTile } from '../ui';
import useMediaQuery, { COMPACT_MAX_LG } from '../../hooks/useMediaQuery';
import {
  formatAgeGroupName,
  formatAgeRange,
  formatAudienceMarketPhrase,
} from '../../utils/scoreHelpers';

const AGE_ORDER = ['13-17', '18-24', '25-34', '35-44', '45+'];
const GENDER_ORDER = ['female', 'male', 'other'];
const GENDER_LABELS = {
  female: 'Female',
  male: 'Male',
  other: 'Other',
};
const GENDER_COLORS = {
  female: '#FF2D92',
  male: '#007AFF',
  other: '#8E8E93',
};
/** Same stroke/fill blue as GenreFitRadar song polygon. */
const GENRE_PROFILE_BLUE = '#007AFF';
const CHART_HEIGHT = 220;
/** Bottom band shared by age X-axis ticks and gender legend (keeps baselines aligned). */
const AXIS_BAND_H = 36;
const CHART_MARGIN = { top: 16, right: 12, left: 4, bottom: 4 };

/** Invisible hit pad around each age bar (short bars stay reachable). */
const BAR_HIT_PAD_X = 10;
const BAR_HIT_PAD_Y = 12;
const BAR_HIT_MIN_H = 32;

/** Kill Recharts wrapper slide-from-(0,0); we pin position to the pointer. */
const TOOLTIP_BASE = {
  isAnimationActive: false,
  animationDuration: 0,
  allowEscapeViewBox: { x: true, y: true },
  cursor: false,
};

/** Pointer offset so the card sits to the top-right of the cursor/tap. */
const TIP_OFFSET_X = 14;
const TIP_OFFSET_Y = -52;

const ChartPointerContext = createContext(null);

function pct(v) {
  return Math.round((Number(v) || 0) * 100);
}

function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <div className="rounded-lg border border-white/10 bg-critic-bg-deep/95 backdrop-blur-xl px-2.5 py-1.5 text-xs shadow-lg pointer-events-none">
      <p className="text-critic-text-primary font-medium">{row.name}</p>
      <p className="text-critic-text-muted tabular-nums mt-0.5">{row.pct}%</p>
    </div>
  );
}

/** Tooltip pinned top-right of the live pointer inside ChartFrame. */
function CursorPinnedTooltip({ shared, trigger = 'hover' }) {
  const pointer = useContext(ChartPointerContext);
  const position =
    pointer != null
      ? { x: pointer.x + TIP_OFFSET_X, y: pointer.y + TIP_OFFSET_Y }
      : undefined;

  return (
    <Tooltip
      {...TOOLTIP_BASE}
      shared={shared}
      trigger={trigger}
      position={position}
      content={<ChartTooltip />}
    />
  );
}

/**
 * Visible bar + larger transparent hit rect (min height + padding).
 * Hit layer is drawn on top so short bars stay easy to hover/tap.
 */
function AgeBarShape(props) {
  const { x, y, width, height, fill } = props;
  const barX = Number(x) || 0;
  const barY = Number(y) || 0;
  const barW = Math.max(Number(width) || 0, 0);
  const barH = Math.max(Number(height) || 0, 0);
  const barBottom = barY + barH;

  const hitH = Math.max(barH, BAR_HIT_MIN_H) + BAR_HIT_PAD_Y * 2;
  const hitW = barW + BAR_HIT_PAD_X * 2;
  const hitX = barX - BAR_HIT_PAD_X;
  // Grow upward from the bar baseline so padding sits around the ink.
  const hitY = barBottom - hitH + BAR_HIT_PAD_Y;

  const isActive = Boolean(props.isActive);
  const ink = isActive ? '#3395FF' : fill;

  return (
    <g className="recharts-bar-rectangle">
      <rect
        x={barX}
        y={barY}
        width={barW}
        height={barH}
        fill={ink}
        rx={4}
        ry={4}
      />
      <rect
        x={hitX}
        y={hitY}
        width={hitW}
        height={hitH}
        fill="transparent"
        style={{ cursor: 'pointer', outline: 'none' }}
        tabIndex={-1}
      />
    </g>
  );
}

/** Pie sector; tooltip-active slice pops slightly outward (Recharts 3 isActive). */
function GenderPieSlice({ isActive, ...sectorProps }) {
  const outer = Number(sectorProps.outerRadius) || 0;
  return (
    <Sector
      {...sectorProps}
      outerRadius={isActive ? outer + 6 : outer}
      stroke="none"
      strokeWidth={0}
    />
  );
}

/** Chart-local pointer from mouse or touch (for top-right tooltip pin). */
function pointerFromEvent(event, el) {
  const rect = el.getBoundingClientRect();
  const src = event.touches?.[0] || event.changedTouches?.[0] || event;
  if (src?.clientX == null) return null;
  return {
    x: src.clientX - rect.left,
    y: src.clientY - rect.top,
  };
}

/**
 * Tracks pointer in chart-local coords for tooltip pinning.
 * Strips Recharts/SVG focus + tap highlight (same issue GenreFitRadar had on mobile).
 * @param {{ children: React.ReactNode, footer?: React.ReactNode }} props
 */
function ChartFrame({ children, footer = null }) {
  const ref = useRef(null);
  const [pointer, setPointer] = useState(null);

  const updatePointer = (event) => {
    const el = ref.current;
    if (!el) return;
    const next = pointerFromEvent(event, el);
    if (next) setPointer(next);
  };

  return (
    <ChartPointerContext.Provider value={pointer}>
      <div
        ref={ref}
        className={[
          'w-full min-w-0 overflow-visible touch-manipulation flex flex-col outline-none',
          // Kill white focus/tap ring on Recharts wrapper + SVG viewBox (Chrome mobile).
          '[&_.recharts-wrapper]:outline-none [&_.recharts-surface]:outline-none',
          '[&_svg]:outline-none [&_*:focus]:outline-none [&_*:focus-visible]:outline-none',
        ].join(' ')}
        style={{ height: CHART_HEIGHT, WebkitTapHighlightColor: 'transparent' }}
        onMouseMove={updatePointer}
        onMouseLeave={() => setPointer(null)}
        onTouchStart={updatePointer}
        onTouchMove={updatePointer}
      >
        <div className="min-h-0 flex-1 w-full outline-none">{children}</div>
        {footer ? (
          <div
            className="shrink-0 w-full flex items-center justify-center"
            style={{ height: AXIS_BAND_H }}
          >
            {footer}
          </div>
        ) : null}
      </div>
    </ChartPointerContext.Provider>
  );
}

/**
 * @param {object} props
 * @param {object} props.demographics
 * @param {string} props.audienceLabel scored lane display label (subgenre when set)
 * @param {boolean} [props.panelReady=true] parent expand finished — remount charts
 * @param {() => void} props.onClose
 * @param {string} [props.className]
 */
export default function AudienceLearnMore({
  demographics,
  audienceLabel,
  panelReady = true,
  onClose,
  className = '',
}) {
  const ageScores = demographics?.ageGroupScores || {};
  const genderScores = demographics?.genderScores || {};
  const isCompact = useMediaQuery(COMPACT_MAX_LG);
  const tipTrigger = isCompact ? 'click' : 'hover';

  const ageData = useMemo(
    () =>
      AGE_ORDER.map((key) => ({
        key,
        range: formatAgeRange(key),
        name: formatAgeGroupName(key),
        pct: pct(ageScores[key]),
      })),
    [ageScores]
  );

  const genderData = useMemo(
    () =>
      GENDER_ORDER.map((key) => ({
        key,
        name: GENDER_LABELS[key],
        pct: pct(genderScores[key]),
        fill: GENDER_COLORS[key],
      })).filter((row) => row.pct > 0),
    [genderScores]
  );

  const hasGender = GENDER_ORDER.some((k) => genderScores[k] != null);

  return (
    <Card
      variant="inset"
      className={`p-4 sm:p-6 relative overflow-visible ${className}`.trim()}
      aria-label="Audience View"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <IconTile size="sm" gradient="orange">
            <Users className="h-4 w-4 text-white" aria-hidden />
          </IconTile>
          <div className="min-w-0">
            <h4 className="font-display text-base sm:text-lg font-semibold text-critic-text-primary truncate">
              Audience · {audienceLabel}
            </h4>
            <p className="text-xs text-critic-text-muted mt-0.5">
              Who you should market to.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 p-2 rounded-lg text-critic-text-muted hover:text-critic-text-primary hover:bg-white/5 transition-colors"
          aria-label="Close audience view"
        >
          <Minimize2 className="h-4 w-4" />
        </button>
      </div>

      {/* Block: Age + Gender; shared bottom band aligns age ticks with gender legend */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 min-w-0">
        <div className="min-w-0" aria-label="Age distribution">
          <p className="text-sm font-medium text-critic-text-primary mb-3">Age</p>
          <ChartFrame
            footer={
              <div
                className="w-full grid text-[11px] text-critic-text-muted"
                style={{
                  paddingLeft: 40,
                  paddingRight: 12,
                  gridTemplateColumns: `repeat(${ageData.length}, minmax(0, 1fr))`,
                }}
              >
                {ageData.map((row) => (
                  <span key={row.key} className="text-center tabular-nums truncate">
                    {row.range}
                  </span>
                ))}
              </div>
            }
          >
            {panelReady ? (
              <ResponsiveContainer width="100%" height="100%" debounce={50}>
                {/* X labels live in ChartFrame footer so they align with gender legend */}
                <BarChart
                  data={ageData}
                  margin={{ ...CHART_MARGIN, bottom: 0 }}
                  style={{ outline: 'none' }}
                  tabIndex={-1}
                >
                  <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                  <XAxis dataKey="range" hide />
                  <YAxis
                    domain={[0, 100]}
                    tick={{ fill: 'rgba(235,235,245,0.45)', fontSize: 10 }}
                    tickLine={false}
                    axisLine={false}
                    width={36}
                    tickFormatter={(v) => `${v}%`}
                  />
                  <CursorPinnedTooltip shared={false} trigger={tipTrigger} />
                  <Bar
                    dataKey="pct"
                    fill={GENRE_PROFILE_BLUE}
                    maxBarSize={40}
                    shape={AgeBarShape}
                    activeBar={AgeBarShape}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : null}
          </ChartFrame>
          {/* Desktop: caption under Age (left column). Mobile: under Gender below. */}
          {demographics?.primaryTarget ? (
            <p className="hidden sm:block text-xs text-critic-text-muted mt-3">
              {formatAudienceMarketPhrase(demographics)}
            </p>
          ) : null}
        </div>

        <div className="min-w-0" aria-label="Gender shares">
          <p className="text-sm font-medium text-critic-text-primary mb-3">Gender</p>
          {hasGender && genderData.length > 0 ? (
            <ChartFrame
              footer={
                <ul className="flex flex-wrap justify-center gap-x-4 gap-y-1" aria-hidden>
                  {genderData.map((row) => (
                    <li
                      key={row.key}
                      className="inline-flex items-center gap-1.5 text-[11px] text-critic-text-muted"
                    >
                      <span
                        className="h-2 w-2 rounded-full shrink-0"
                        style={{ backgroundColor: row.fill }}
                      />
                      <span>
                        {row.name}{' '}
                        <span className="tabular-nums text-critic-text-primary">{row.pct}%</span>
                      </span>
                    </li>
                  ))}
                </ul>
              }
            >
              {panelReady ? (
                <ResponsiveContainer width="100%" height="100%" debounce={50}>
                  <PieChart style={{ outline: 'none' }} tabIndex={-1}>
                    <Pie
                      data={genderData}
                      dataKey="pct"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius="48%"
                      outerRadius="88%"
                      paddingAngle={2}
                      shape={GenderPieSlice}
                    >
                      {genderData.map((row) => (
                        <Cell
                          key={row.key}
                          fill={row.fill}
                          stroke="rgba(10,10,15,0.55)"
                          strokeWidth={1}
                        />
                      ))}
                    </Pie>
                    <CursorPinnedTooltip trigger={tipTrigger} />
                  </PieChart>
                </ResponsiveContainer>
              ) : null}
            </ChartFrame>
          ) : (
            <p className="text-xs text-critic-text-muted">Gender priors unavailable for this session.</p>
          )}
          {/* Mobile (stacked): after Gender so the caption is not between the two charts. */}
          {demographics?.primaryTarget ? (
            <p className="sm:hidden text-xs text-critic-text-muted mt-3">
              {formatAudienceMarketPhrase(demographics)}
            </p>
          ) : null}
        </div>
      </div>
    </Card>
  );
}
